import { Button, Skeleton } from '@design-system';
import { Loader2, Save } from 'lucide-react';
import { type FundingPolicyType } from '@shared';
import { FormEditorSidebar, FormEditorContent } from '@/features/quote-and-apply-builder';
import {
  useFormEditorPage,
  useFormEditorLoaded,
} from '@/features/quote-and-apply-builder/hooks/use-form-editor-page';

export function FormEditorPage() {
  const { carrierId, formConfig, isLoading, error, handleBack } = useFormEditorPage();

  if (isLoading) {
    return <FormEditorSkeleton />;
  }

  if (error || !formConfig) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="mb-2 text-red-600">Failed to load form.</p>
          <p className="text-sm text-gray-500">Please try again or go back.</p>
        </div>
      </div>
    );
  }

  return (
    <FormEditorLoaded
      formConfig={formConfig}
      carrierId={carrierId}
      onBack={handleBack}
    />
  );
}

export interface FormEditorLoadedProps {
  formConfig: PolicyFormConfig;
  carrierId: string;
  fundingPolicyTypeId?: FundingPolicyType[];
  stateCode?: string;
  onBack: () => void;
}

export function FormEditorLoaded({
  formConfig,
  fundingPolicyTypeId,
  stateCode,
  onBack,
}: FormEditorLoadedProps) {
  const { canSave, isSaving, handleSave } = useFormEditorLoaded({
    formConfig,
    fundingPolicyTypeId,
    stateCode,
  });

  return (
    <div className="flex h-screen overflow-hidden">
      <FormEditorSidebar onBack={onBack} />

      <FormEditorContent />

      {canSave && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Button
            size="lg"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

function FormEditorSkeleton() {
  return (
    <div className="flex h-screen overflow-hidden">
      <div className="hidden w-80 border-r bg-white p-6 md:block">
        <div className="mb-6 flex items-center gap-4">
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-6 w-40" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton
              key={index}
              className="h-8 w-full"
            />
          ))}
        </div>
      </div>
      <div className="flex-1 bg-gray-50 p-8">
        <Skeleton className="mb-4 h-8 w-64" />
        <div className="mb-6 flex gap-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              key={index}
              className="h-9 w-24 rounded-md"
            />
          ))}
        </div>
        <Skeleton className="h-64 w-full rounded-md" />
      </div>
    </div>
  );
}
